import type { UnitStateV2 } from '@eppendorf/vnls-device-service-utils';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DotsLoader,
  Icon,
  IconSizeClasses,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ActivityStatus } from '$components/device/activity-status/activity-status';
import { SearchInput } from '$components/search-input/search-input';
import { Pagination } from '$components/table/pagination/pagination';
import { searchDeviceIcon } from '$features/devices/devices.utils';
import { useDeviceSnapshot } from '$features/monitoring/device-snapshot.api';
import { useDeviceTelemetryDataForSnapshot } from '$features/monitoring/parameters/useDeviceTelemetryDataForSnapshot';
import { createDevicesV2QueryWithQueryParams } from '$features/monitoring/v2/devicesv2.api';

import type { DashboardTileAvailability } from '../dashboard-availability';
import { normalizeDashboardPreferences } from '../dashboard-preferences';
import type {
  DashboardPreferences,
  DashboardSelectedDevice,
  DashboardTileId,
} from '../dashboard.types';

import './dashboard-settings-dialog.scss';

const DEVICE_PAGE_SIZE = 10;

interface DashboardSettingsButtonProps {
  onClick: () => void;
}

/* eslint-disable @typescript-eslint/no-use-before-define -- Keep public components before colocated implementation details. */

export function DashboardSettingsButton({ onClick }: DashboardSettingsButtonProps) {
  const { t } = useTranslation();

  return (
    <Tooltip
      trigger={
        <Button
          type="button"
          variant="icon"
          aria-label={t('dashboard.settings.open')}
          onClick={onClick}
        >
          <Icon name="settings" size={IconSizeClasses.Small} />
        </Button>
      }
    >
      {t('dashboard.settings.open')}
    </Tooltip>
  );
}

interface DashboardSettingsDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  tiles: DashboardTileAvailability[];
  preferences: DashboardPreferences;
  onSave: (preferences: DashboardPreferences) => boolean;
}

export function DashboardSettingsDialog({
  isOpen,
  onOpenChange,
  tiles,
  preferences,
  onSave,
}: DashboardSettingsDialogProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="w-max-4xl dashboard-settings-dialog"
      hideCloseButton
    >
      {isOpen && (
        <DashboardSettingsForm
          tiles={tiles}
          preferences={preferences}
          onCancel={() => onOpenChange(false)}
          onSave={(nextPreferences) => {
            const didSave = onSave(nextPreferences);
            if (didSave) onOpenChange(false);
            return didSave;
          }}
        />
      )}
    </Dialog>
  );
}

interface DashboardSettingsFormProps {
  tiles: DashboardTileAvailability[];
  preferences: DashboardPreferences;
  onCancel: () => void;
  onSave: (preferences: DashboardPreferences) => boolean;
}

function DashboardSettingsForm({
  tiles,
  preferences,
  onCancel,
  onSave,
}: DashboardSettingsFormProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSaveError, setHasSaveError] = useState(false);
  const { control, handleSubmit, setValue } = useForm<DashboardPreferences>({
    defaultValues: preferences,
  });
  const hiddenTileIds = useWatch({ control, name: 'hiddenTileIds' }) ?? [];
  const selectedDevices = useWatch({ control, name: 'selectedDevices' }) ?? [];
  const availableTileIds = tiles.filter((tile) => tile.available).map((tile) => tile.id);
  const canConfigureDevices = availableTileIds.includes('deviceOverview');
  const hasHiddenAvailableTile = availableTileIds.some((tileId) =>
    hiddenTileIds.includes(tileId),
  );
  const devicesQuery = useQuery({
    ...createDevicesV2QueryWithQueryParams({
      page,
      pageSize: DEVICE_PAGE_SIZE,
      searchQuery,
      connectionStatus: 'paired',
    }),
    enabled: canConfigureDevices,
  });

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const tileLabels = new Map<DashboardTileId, string>([
    ['labHealth', t('dashboard.labHealth.title')],
    ['deviceOverview', t('dashboard.deviceOverview.title')],
    ['recentDeviations', t('dashboard.recentDeviations.title')],
    ['incidents', t('dashboard.alertsIncidents.title')],
    ['auditTrail', t('dashboard.latestAuditTrail.title')],
    ['pendingInvitations', t('dashboard.pendingInvitations.title')],
    ['reports', t('dashboard.latestReports.title')],
  ]);

  const accessReasonLabels = new Map([
    ['role', t('dashboard.settings.requiresRole')],
    ['subscription', t('dashboard.settings.requiresSubscription')],
    ['entitlement', t('dashboard.settings.requiresEntitlement')],
  ]);

  const setTileVisible = (tileId: DashboardTileId, isVisible: boolean) => {
    const nextHiddenTileIds = isVisible
      ? hiddenTileIds.filter((hiddenTileId) => hiddenTileId !== tileId)
      : [...new Set([...hiddenTileIds, tileId])];
    setValue('hiddenTileIds', nextHiddenTileIds, { shouldDirty: true });
  };

  const setAllTilesVisible = (isVisible: boolean) => {
    const nextHiddenTileIds = isVisible
      ? hiddenTileIds.filter((tileId) => !availableTileIds.includes(tileId))
      : [...new Set([...hiddenTileIds, ...availableTileIds])];
    setValue('hiddenTileIds', nextHiddenTileIds, { shouldDirty: true });
  };

  const toggleDevice = (manufacturer: string, serialNumber: string) => {
    const isSelected = selectedDevices.some(
      (device) =>
        device.manufacturer === manufacturer && device.serialNumber === serialNumber,
    );
    const nextSelectedDevices = isSelected
      ? selectedDevices.filter(
          (device) =>
            device.manufacturer !== manufacturer || device.serialNumber !== serialNumber,
        )
      : [...selectedDevices, { manufacturer, serialNumber, telemetryIds: [] }].slice(
          0,
          3,
        );

    setValue('selectedDevices', nextSelectedDevices, { shouldDirty: true });
  };

  const updateTelemetryIds = (
    selectedDevice: DashboardSelectedDevice,
    telemetryIds: string[],
  ) => {
    setValue(
      'selectedDevices',
      selectedDevices.map((device) =>
        device.manufacturer === selectedDevice.manufacturer &&
        device.serialNumber === selectedDevice.serialNumber
          ? { ...device, telemetryIds: [...new Set(telemetryIds)].slice(0, 2) }
          : device,
      ),
      { shouldDirty: true },
    );
  };

  const save = (draft: DashboardPreferences) => {
    const didSave = onSave(normalizeDashboardPreferences(draft));
    setHasSaveError(!didSave);
  };

  return (
    <form onSubmit={handleSubmit(save)}>
      <Dialog.Title>{t('dashboard.settings.title')}</Dialog.Title>
      <Dialog.Description>{t('dashboard.settings.description')}</Dialog.Description>

      <div className="dashboard-settings-dialog__body">
        <section className="dashboard-settings-dialog__section">
          <div className="dashboard-settings-dialog__widget-heading">
            <h4>{t('dashboard.settings.tilesTitle')}</h4>
            <Button
              type="button"
              variant="tertiary"
              size="small"
              onClick={() => setAllTilesVisible(hasHiddenAvailableTile)}
              disabled={availableTileIds.length === 0}
            >
              {t(
                hasHiddenAvailableTile
                  ? 'dashboard.settings.selectAllWidgets'
                  : 'dashboard.settings.clearAllWidgets',
              )}
            </Button>
          </div>
          <div className="dashboard-settings-dialog__tile-grid">
            {tiles.map(({ id: tileId, available, reasons }) => {
              const isVisible = available && !hiddenTileIds.includes(tileId);
              const inputId = `dashboard-tile-${tileId}`;

              return (
                <div className="dashboard-settings-dialog__tile-row" key={tileId}>
                  <Checkbox
                    id={inputId}
                    checked={isVisible}
                    disabled={!available}
                    ariaDescribedBy={reasons.length ? `${inputId}-reasons` : undefined}
                    onChange={(checked) => setTileVisible(tileId, checked === true)}
                  />
                  <div className="dashboard-settings-dialog__tile-label">
                    <label htmlFor={inputId}>{tileLabels.get(tileId)}</label>
                    {reasons.length > 0 && (
                      <ul
                        id={`${inputId}-reasons`}
                        className="dashboard-settings-dialog__access-reasons"
                      >
                        {reasons.map((reason) => (
                          <li key={reason}>{accessReasonLabels.get(reason)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="dashboard-settings-dialog__tile-row">
              <Badge color="blue" size="small">
                {t('dashboard.settings.alwaysOn')}
              </Badge>
              <span>{t('dashboard.quickActions.title')}</span>
            </div>
          </div>
        </section>

        {canConfigureDevices && (
          <section className="dashboard-settings-dialog__section">
            <div className="dashboard-settings-dialog__section-heading">
              <div>
                <h4>{t('dashboard.settings.devicesTitle')}</h4>
                <p>{t('dashboard.settings.devicesDescription')}</p>
              </div>
              <span>
                {t('dashboard.settings.devicesSelected', {
                  selected: selectedDevices.length,
                  maximum: 3,
                })}
              </span>
            </div>

            <SearchInput
              value={searchQuery}
              onSearchQueryChange={handleSearch}
              placeholder={t('dashboard.settings.searchDevices')}
              className="dashboard-settings-dialog__search"
            />

            {selectedDevices.length > 0 && (
              <div className="dashboard-settings-dialog__device-list m-bottom-m">
                {selectedDevices.map((selectedDevice) => (
                  <SelectedDeviceRow
                    key={`${selectedDevice.manufacturer}-${selectedDevice.serialNumber}`}
                    selectedDevice={selectedDevice}
                    onRemove={() =>
                      toggleDevice(
                        selectedDevice.manufacturer,
                        selectedDevice.serialNumber,
                      )
                    }
                    onChange={(telemetryIds) =>
                      updateTelemetryIds(selectedDevice, telemetryIds)
                    }
                  />
                ))}
              </div>
            )}

            {devicesQuery.isLoading && <DotsLoader className="m-y-l" />}
            {devicesQuery.isError && (
              <div className="dashboard-settings-dialog__inline-feedback">
                <span>{t('dashboard.settings.devicesLoadError')}</span>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    devicesQuery.refetch();
                  }}
                >
                  {t('dashboard.settings.retry')}
                </Button>
              </div>
            )}
            {devicesQuery.data && devicesQuery.data.data.length === 0 && (
              <div className="dashboard-settings-dialog__empty">
                <Icon
                  name="no-results"
                  size={IconSizeClasses.Large}
                  className="bg-gray-500 m-bottom-l"
                />
                <p>{t('dashboard.settings.noDevices')}</p>
                {searchQuery && (
                  <Button
                    type="button"
                    variant="outline"
                    size="small"
                    className="m-top-m"
                    onClick={() => handleSearch('')}
                  >
                    {t('dashboard.settings.resetSearch')}
                  </Button>
                )}
              </div>
            )}
            {devicesQuery.data && devicesQuery.data.data.length > 0 && (
              <div className="dashboard-settings-dialog__device-list">
                {devicesQuery.data.data
                  .filter(
                    (device) =>
                      !selectedDevices.some(
                        (selectedDevice) =>
                          selectedDevice.manufacturer === device.manufacturer &&
                          selectedDevice.serialNumber === device.serialNumber,
                      ),
                  )
                  .map((device) => {
                    const isDisabled = selectedDevices.length >= 3;
                    const inputId = `dashboard-device-${device.manufacturer}-${device.serialNumber}`;
                    const location = [
                      device.location?.site,
                      device.location?.building,
                      device.location?.room,
                      device.location?.position,
                    ]
                      .filter(Boolean)
                      .join(' / ');

                    return (
                      <div
                        className="dashboard-settings-dialog__device-row"
                        key={inputId}
                      >
                        {isDisabled ? (
                          <Tooltip
                            trigger={
                              <span className="dashboard-settings-dialog__disabled-control">
                                <Checkbox id={inputId} checked={false} disabled />
                              </span>
                            }
                          >
                            {t('dashboard.settings.devicesSelectionLimit')}
                          </Tooltip>
                        ) : (
                          <Checkbox
                            id={inputId}
                            checked={false}
                            onChange={() =>
                              toggleDevice(device.manufacturer, device.serialNumber)
                            }
                          />
                        )}
                        <Icon
                          name={searchDeviceIcon(device.type, undefined, device.model)}
                          size={IconSizeClasses.Small}
                        />
                        <label htmlFor={inputId}>
                          <strong>{device.name || device.serialNumber}</strong>
                          <span>
                            {[device.serialNumber, location].filter(Boolean).join(' - ')}
                          </span>
                        </label>
                        <div className="dashboard-settings-dialog__device-status">
                          <ActivityStatus
                            activityStatus={(device.activityState as UnitStateV2) || null}
                            bordered={false}
                            size="small"
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {devicesQuery.data && devicesQuery.data.pagination.totalPages > 1 && (
              <Pagination
                paginationData={devicesQuery.data.pagination}
                showItemRange={false}
                showPageSize={false}
                onPageChange={setPage}
                onPageSizeChange={() => {}}
              />
            )}
          </section>
        )}
      </div>

      {hasSaveError && (
        <p className="dashboard-settings-dialog__save-error" role="alert">
          {t('dashboard.settings.saveError')}
        </p>
      )}

      <Dialog.Actions className="m-top-xxl">
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('dashboard.settings.cancel')}
        </Button>
        <Button type="submit">{t('dashboard.settings.save')}</Button>
      </Dialog.Actions>
    </form>
  );
}

interface SelectedDeviceRowProps {
  selectedDevice: DashboardSelectedDevice;
  onRemove: () => void;
  onChange: (telemetryIds: string[]) => void;
}

function SelectedDeviceRow({
  selectedDevice,
  onRemove,
  onChange,
}: SelectedDeviceRowProps) {
  const snapshotQuery = useDeviceSnapshot({
    manufacturer: selectedDevice.manufacturer,
    serialNumber: selectedDevice.serialNumber,
  });
  const inventoryDevice = snapshotQuery.data?.inventoryDevice;
  const deviceName = inventoryDevice?.name || selectedDevice.serialNumber;
  const inputId = `dashboard-device-${selectedDevice.manufacturer}-${selectedDevice.serialNumber}`;

  return (
    <div className="dashboard-settings-dialog__device-row">
      <Checkbox id={inputId} checked onChange={onRemove} />
      <Icon
        name={searchDeviceIcon(inventoryDevice?.type, undefined, inventoryDevice?.model)}
        size={IconSizeClasses.Small}
      />
      <label htmlFor={inputId}>
        <strong>{deviceName}</strong>
        {deviceName !== selectedDevice.serialNumber && (
          <span>{selectedDevice.serialNumber}</span>
        )}
      </label>
      <div className="dashboard-settings-dialog__device-status">
        <ActivityStatus
          activityStatus={snapshotQuery.data?.currentShadow.activityState || null}
          bordered={false}
          size="small"
        />
      </div>
      <DeviceParameterPicker selectedDevice={selectedDevice} onChange={onChange} />
    </div>
  );
}

interface DeviceParameterPickerProps {
  selectedDevice: DashboardSelectedDevice;
  onChange: (telemetryIds: string[]) => void;
}

function DeviceParameterPicker({ selectedDevice, onChange }: DeviceParameterPickerProps) {
  const { t } = useTranslation();
  const snapshotQuery = useDeviceSnapshot({
    manufacturer: selectedDevice.manufacturer,
    serialNumber: selectedDevice.serialNumber,
  });
  const telemetry = useDeviceTelemetryDataForSnapshot(snapshotQuery.data);
  const chartableParameters = [
    ...telemetry.primaryParameters,
    ...telemetry.secondaryParameters,
  ].filter(
    (parameter, index, parameters) =>
      parameter.valueType === 'DOUBLE' &&
      parameters.findIndex((item) => item.telemetryId === parameter.telemetryId) ===
        index,
  );
  const availableTelemetryIds = new Set(
    chartableParameters.map((parameter) => parameter.telemetryId),
  );
  const unavailableTelemetryIds = selectedDevice.telemetryIds.filter(
    (telemetryId) => !availableTelemetryIds.has(telemetryId),
  );
  const toggleParameter = (telemetryId: string) => {
    const isSelected = selectedDevice.telemetryIds.includes(telemetryId);
    onChange(
      isSelected
        ? selectedDevice.telemetryIds.filter((id) => id !== telemetryId)
        : [...selectedDevice.telemetryIds, telemetryId],
    );
  };

  return (
    <div className="dashboard-settings-dialog__device-parameters">
      {snapshotQuery.isLoading && <DotsLoader />}
      {snapshotQuery.isError && (
        <div className="dashboard-settings-dialog__inline-feedback">
          <span>{t('dashboard.settings.parametersLoadError')}</span>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => {
              snapshotQuery.refetch();
            }}
          >
            {t('dashboard.settings.retry')}
          </Button>
        </div>
      )}
      {snapshotQuery.data &&
        chartableParameters.length === 0 &&
        unavailableTelemetryIds.length === 0 && (
          <p className="dashboard-settings-dialog__empty">
            {t('dashboard.settings.noParameters')}
          </p>
        )}
      {(chartableParameters.length > 0 || unavailableTelemetryIds.length > 0) && (
        <div className="dashboard-settings-dialog__parameter-list">
          {chartableParameters.map((parameter) => {
            const isSelected = selectedDevice.telemetryIds.includes(
              parameter.telemetryId,
            );
            const inputId = `dashboard-parameter-${selectedDevice.serialNumber}-${parameter.telemetryId}`;

            return (
              <div className="dashboard-settings-dialog__checkbox-row" key={inputId}>
                <Checkbox
                  id={inputId}
                  checked={isSelected}
                  disabled={!isSelected && selectedDevice.telemetryIds.length >= 2}
                  onChange={() => toggleParameter(parameter.telemetryId)}
                />
                <label htmlFor={inputId}>{parameter.name || parameter.telemetryId}</label>
              </div>
            );
          })}
          {unavailableTelemetryIds.map((telemetryId) => {
            const inputId = `dashboard-parameter-${selectedDevice.serialNumber}-${telemetryId}`;
            return (
              <div className="dashboard-settings-dialog__checkbox-row" key={inputId}>
                <Checkbox
                  id={inputId}
                  checked
                  onChange={() => toggleParameter(telemetryId)}
                />
                <label htmlFor={inputId}>
                  {t('dashboard.settings.unavailableParameter', { telemetryId })}
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* eslint-enable @typescript-eslint/no-use-before-define */
